import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Select, Typography } from 'antd';
import { ArrowRightOutlined, CloseCircleFilled } from '@ant-design/icons';
import { IMPORT_TARGET_FIELD_GROUPS, IMPORT_SECTIONS } from '../../services/customerImportApi';

const { Text } = Typography;

// Mirrors frontend/src/components/ContractorsHub/PipesPanel.jsx's MappingCanvas
// connector shape exactly (Story 2.1, user-directed) -- a bezier curve from a
// source node's right edge to a target node's left edge.
const connectorPath = (x1, y1, x2, y2) => {
  const dx = Math.max(40, (x2 - x1) / 2);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
};

const NODE_HEIGHT = 36;
const NODE_GAP = 8;
const LINE_COLOR = '#1677ff';

// All target field paths across every group, flattened -- used for required-
// field validation and for looking up a field's label/required flag by path.
const ALL_TARGET_FIELDS = IMPORT_TARGET_FIELD_GROUPS.flatMap((g) => g.fields);

// A target entity (customer/address/contact) is only "attempted" -- and so
// only subject to its own required-field check -- once the admin has mapped
// at least one of its fields. Mapping zero address fields is fine (not every
// row needs a ship-to); mapping address_line1 but not city is not.
export const computeMappingValidation = (mapping) => {
  const missingByGroup = {};
  let allRequiredMapped = true;
  IMPORT_TARGET_FIELD_GROUPS.forEach((group) => {
    const anyMapped = group.fields.some((f) => mapping[f.path]);
    if (!anyMapped) return;
    const missing = group.fields.filter((f) => f.required && !mapping[f.path]);
    if (missing.length) {
      missingByGroup[group.entity] = missing;
      allRequiredMapped = false;
    }
  });
  const hasAnyMapping = Object.keys(mapping || {}).length > 0;
  return { missingByGroup, isValid: hasAnyMapping && allRequiredMapped };
};

/**
 * Drag-and-drop, connector-style field-mapping canvas (Story 2.1 AC #2) --
 * mirrors ContractorsHub's PipesPanel.jsx MappingCanvas pattern: native HTML5
 * drag-and-drop, an absolutely-positioned SVG overlay with bezier connector
 * lines recomputed from live DOM node positions. Differences from the
 * original (see story Dev Notes): source nodes are the uploaded file's
 * column headers (not a static product-field catalog); target nodes are
 * grouped by entity (Customer/Ship-To/Contact) with required markers; no
 * mode toggle (field-only, no value/concat); no persistent save -- `mapping`
 * is lifted to the parent via `onChange`.
 */
const CustomerImportMappingCanvas = ({ headers, mapping, onChange }) => {
  const [lines, setLines] = useState([]);
  const [dragSourceKey, setDragSourceKey] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  const containerRef = useRef(null);
  const sourceRefs = useRef({});
  const targetRefs = useRef({});

  const sourceFieldList = useMemo(() => (headers || []).map((h) => ({ path: h, label: h })), [headers]);

  const recomputeLines = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerBox = container.getBoundingClientRect();
    const anchor = (el, side) => {
      const box = el.getBoundingClientRect();
      return {
        x: (side === 'right' ? box.right : box.left) - containerBox.left,
        y: box.top + box.height / 2 - containerBox.top,
      };
    };

    const next = [];
    Object.entries(mapping || {}).forEach(([target, source]) => {
      if (!source) return;
      const sourceEl = sourceRefs.current[source];
      const targetEl = targetRefs.current[target];
      if (!sourceEl || !targetEl) return;
      const from = anchor(sourceEl, 'right');
      const to = anchor(targetEl, 'left');
      next.push({ key: target, ...from, x2: to.x, y2: to.y });
    });
    setLines(next);
  }, [mapping]);

  useLayoutEffect(() => {
    recomputeLines();
  }, [recomputeLines]);

  useEffect(() => {
    window.addEventListener('resize', recomputeLines);
    return () => window.removeEventListener('resize', recomputeLines);
  }, [recomputeLines]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(recomputeLines);
    observer.observe(container);
    return () => observer.disconnect();
  }, [recomputeLines]);

  const handleDragStart = (sourceKey) => (e) => {
    e.dataTransfer.setData('text/plain', sourceKey);
    e.dataTransfer.effectAllowed = 'copy';
    setDragSourceKey(sourceKey);
  };

  const handleDragEnd = () => {
    setDragSourceKey(null);
    setDragOverTarget(null);
  };

  const handleDragOverTarget = (targetKey) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverTarget(targetKey);
  };

  const handleDrop = (targetKey) => (e) => {
    e.preventDefault();
    const sourceKey = e.dataTransfer.getData('text/plain');
    if (sourceKey) onChange({ ...mapping, [targetKey]: sourceKey });
    setDragSourceKey(null);
    setDragOverTarget(null);
  };

  const handleRemoveRow = (targetKey) => {
    const next = { ...mapping };
    delete next[targetKey];
    onChange(next);
  };

  const handleSelectSource = (targetKey) => (value) => {
    if (value) {
      onChange({ ...mapping, [targetKey]: value });
    } else {
      handleRemoveRow(targetKey);
    }
  };

  const { missingByGroup } = computeMappingValidation(mapping);
  const headerOptions = (headers || []).map((h) => ({ value: h, label: h }));
  const mappedCount = Object.keys(mapping || {}).length;
  const totalFieldCount = ALL_TARGET_FIELDS.length;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Drag a column from your file onto a field below, or pick it from the dropdown, working
          through the three sections in order: Customer &amp; Bill-To Address, then Ship-To Address,
          then Contacts. Required fields for an entity only apply once you&apos;ve mapped at least
          one field for that entity -- a row doesn&apos;t need a bill-to, a ship-to address, or a
          contact. Click the <CloseCircleFilled style={{ color: '#999' }} /> to remove a mapping.
        </Text>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div ref={containerRef} style={{ position: 'relative', display: 'flex', flex: 1, minWidth: 0, gap: 100, alignItems: 'flex-start' }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          {lines.map((line) => (
            <g key={line.key}>
              <path d={connectorPath(line.x, line.y, line.x2, line.y2)} stroke={LINE_COLOR} strokeWidth={2} fill="none" />
              <circle cx={line.x} cy={line.y} r={4} fill={LINE_COLOR} />
              <circle cx={line.x2} cy={line.y2} r={4} fill={LINE_COLOR} />
            </g>
          ))}
        </svg>

        <div style={{ width: 240, flexShrink: 0 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Your File Columns</Text>
          {sourceFieldList.map(({ path, label }) => (
            <div
              key={path}
              ref={(el) => { sourceRefs.current[path] = el; }}
              draggable
              onDragStart={handleDragStart(path)}
              onDragEnd={handleDragEnd}
              style={{
                display: 'flex',
                alignItems: 'center',
                height: NODE_HEIGHT,
                marginBottom: NODE_GAP,
                padding: '0 10px',
                background: dragSourceKey === path ? '#e6f4ff' : '#fff',
                border: `1px solid ${dragSourceKey === path ? LINE_COLOR : 'rgba(0,0,0,0.15)'}`,
                borderRadius: 6,
                cursor: 'grab',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              <Text>{label}</Text>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {IMPORT_SECTIONS.map((section) => {
            const groups = IMPORT_TARGET_FIELD_GROUPS.filter((g) => section.entities.includes(g.entity));
            return (
              <div
                key={section.key}
                style={{
                  marginBottom: 20,
                  padding: '14px 16px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 8,
                  background: '#fff',
                }}
              >
                <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 15 }}>{section.title}</Text>
                {groups.map((group) => (
                  <div key={group.entity} style={{ marginBottom: 16 }}>
                    {groups.length > 1 && (
                      <Text
                        type="secondary"
                        style={{
                          display: 'block', marginBottom: 8, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5,
                        }}
                      >
                        {group.label}
                      </Text>
                    )}
                    {group.fields.map((field) => {
                      const target = field.path;
                      const source = mapping[target];
                      const isDragOver = dragOverTarget === target;
                      const isMissingRequired = (missingByGroup[group.entity] || []).some((f) => f.path === target);
                      return (
                        <div
                          key={target}
                          data-testid={`mapping-row-${target}`}
                          ref={(el) => { targetRefs.current[target] = el; }}
                          onDragOver={handleDragOverTarget(target)}
                          onDragLeave={() => setDragOverTarget((current) => (current === target ? null : current))}
                          onDrop={handleDrop(target)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            height: NODE_HEIGHT,
                            marginBottom: NODE_GAP,
                            padding: '0 11px',
                            background: isDragOver ? '#e6f4ff' : '#fff',
                            border: `1px dashed ${isDragOver ? LINE_COLOR : isMissingRequired ? '#ff4d4f' : 'rgba(0,0,0,0.15)'}`,
                            borderRadius: 6,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <Text>{field.label}</Text>
                            {field.required && (
                              <Text type="danger" style={{ marginLeft: 6, fontSize: 12 }}>required</Text>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <Select
                              size="small"
                              style={{ width: 200 }}
                              placeholder="Drop or choose a column"
                              allowClear
                              showSearch
                              value={source || undefined}
                              onChange={handleSelectSource(target)}
                              options={headerOptions}
                              optionFilterProp="label"
                            />
                            {source && (
                              <CloseCircleFilled
                                onClick={() => handleRemoveRow(target)}
                                style={{ cursor: 'pointer', color: '#999' }}
                                aria-label={`Remove mapping for ${field.label}`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          width: 260,
          flexShrink: 0,
          position: 'sticky',
          top: 16,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 8,
          padding: 12,
          background: '#fafafa',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <Text strong>Mapping Progress</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{mappedCount}/{totalFieldCount}</Text>
        </div>
        {IMPORT_TARGET_FIELD_GROUPS.map((group) => {
          const mappedFields = group.fields.filter((f) => mapping[f.path]);
          return (
            <div key={group.entity} style={{ marginBottom: 14 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                {group.label} ({mappedFields.length}/{group.fields.length})
              </Text>
              {mappedFields.length === 0 ? (
                <Text type="secondary" italic style={{ fontSize: 12 }}>No fields mapped yet</Text>
              ) : mappedFields.map((field) => (
                <div
                  key={field.path}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 4,
                  }}
                >
                  <Text style={{ fontSize: 12, flexShrink: 0, maxWidth: 100 }} ellipsis={{ tooltip: field.label }}>
                    {field.label}
                  </Text>
                  <ArrowRightOutlined style={{ color: LINE_COLOR, fontSize: 10, flexShrink: 0 }} />
                  <Text type="secondary" style={{ fontSize: 12, minWidth: 0 }} ellipsis={{ tooltip: mapping[field.path] }}>
                    {mapping[field.path]}
                  </Text>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};

export { ALL_TARGET_FIELDS };
export default CustomerImportMappingCanvas;
